import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from './PaginationNew';
import { CommunityService } from '../services/communityService';
import type { CommunityPost } from '../types/community';

const PendingCommunityPostsTable: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 20,
  });

  const [rows, setRows] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  useEffect(() => {
    document.title = currentPage > 1 ? `Pending Posts - Page ${currentPage}` : 'Pending Posts';
  }, [currentPage]);

  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await CommunityService.listPosts({
          page: currentPage,
          limit: itemsPerPage,
          sort: '-createdAt',
          status: 'pending',
        });

        if (!isMounted) return;
        setRows(res.items || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.items || []).length);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load pending posts');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, currentPage, itemsPerPage]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN');
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const formatCell = (value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'string') return value || '—';
    if (typeof value === 'number') return value.toLocaleString('en-IN');
    return '—';
  };

  const getUserLabel = (post: CommunityPost): string => {
    const v: any = post.userId ?? post.user;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const email = typeof v.email === 'string' ? v.email.trim() : '';
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
    }
    return '—';
  };

  const safeRows = useMemo(() => rows, [rows]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access Pending Posts.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (loading && safeRows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading pending posts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Community Posts</h1>
          <p className="mt-1 text-sm text-gray-500">Posts awaiting approval (Admin only)</p>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Where</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeRows.map((post) => (
                <tr key={post._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">{formatCell(post.title)}</div>
                    <div className="text-xs text-gray-500 break-all">{post._id}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">₹{formatCell(post.amount)}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{formatCell(post.where)}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{getUserLabel(post)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateValue(post.createdAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      onClick={() => navigate(`/community/${encodeURIComponent(String(post._id))}`)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && safeRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                    No pending posts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
};

export default PendingCommunityPostsTable;
